import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';

const STYLE_ID = 'rf-theme-reveal';
const DURATION_MS = 750;
// Доля радиуса маски, где новая тема видна полностью; дальше — мягкая кромка до прозрачности.
// Конечный размер маски считается от этой доли: к последнему кадру сплошная часть накрывает
// самый дальний угол окна, и снятие View Transition ничего не дорисовывает рывком.
const SOLID_FRACTION = 0.7;

const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Смена темы с раскрытием круга от элемента `ref` через View Transition API.
 * Анимируется маска снимка, а не clip-path и не WAAPI: WebKit игнорирует и то и другое
 * на псевдоэлементах ::view-transition-*.
 */
export const useThemeReveal = <T extends HTMLElement>() => {
    const ref = useRef<T>(null);

    const reveal = useCallback(async (apply: () => void) => {
        const origin = ref.current;

        if (!origin || typeof document.startViewTransition !== 'function' || prefersReducedMotion()) {
            apply();
            return;
        }

        const { left, top, width, height } = origin.getBoundingClientRect();
        const x = left + width / 2;
        const y = top + height / 2;
        const { innerWidth: w, innerHeight: h } = window;
        const reach = Math.max(Math.hypot(x, y), Math.hypot(w - x, y), Math.hypot(x, h - y), Math.hypot(w - x, h - y));
        const size = Math.ceil((reach / SOLID_FRACTION) * 2);

        document.getElementById(STYLE_ID)?.remove();
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = `
            ::view-transition-old(root) {
                animation: none;
                mix-blend-mode: normal;
            }
            ::view-transition-new(root) {
                mix-blend-mode: normal;
                mask: radial-gradient(closest-side, #000 ${SOLID_FRACTION * 100}%, transparent) ${x}px ${y}px / 0 0
                    no-repeat;
                animation: ${STYLE_ID} ${DURATION_MS}ms ease-in-out both;
            }
            @keyframes ${STYLE_ID} {
                to {
                    mask-size: ${size}px ${size}px;
                    mask-position: ${x - size / 2}px ${y - size / 2}px;
                }
            }
        `;
        document.head.appendChild(style);

        try {
            await document.startViewTransition(() => flushSync(apply)).finished;
        } catch {
            // Переход пропущен или прерван следующим — тема уже применена, остаётся убрать стили.
        } finally {
            style.remove();
        }
    }, []);

    return { ref, reveal };
};
